import React, { ReactNode } from 'react';

import clsx from 'clsx';

import styles from './Button.module.scss';
import { DesignSystemTypography } from '../../tokens/tokens';
import { Typography } from '../Typography/Typography';

export enum ButtonVariant {
  ACCENT = 'accent',
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  TEXT = 'text',
  DESTRUCTIVE = 'destructive',
}

export enum ButtonSize {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
}

export enum ButtonContentAlign {
  CENTER = 'center',
  START = 'start',
}

type GetButtonClassesOptions = {
  variant: ButtonVariant;
  size?: ButtonSize;
  isFullWidth?: boolean;
  isLoading?: boolean;
  hasStartIcon?: boolean;
  hasEndIcon?: boolean;
  disabled?: boolean;
  contentAlign?: ButtonContentAlign;
  className?: string;
};

const buttonTextBySize: Record<ButtonSize, DesignSystemTypography> = {
  [ButtonSize.SMALL]: DesignSystemTypography.CAPTION_REGULAR,
  [ButtonSize.MEDIUM]: DesignSystemTypography.LABEL_2_REGULAR,
  [ButtonSize.LARGE]: DesignSystemTypography.LABEL_1_SEMIBOLD,
};

export const getButtonTypographyVariant = (
  size = ButtonSize.MEDIUM,
): DesignSystemTypography => {
  return buttonTextBySize[size];
};

export const getButtonClasses = ({
  variant,
  size = ButtonSize.MEDIUM,
  isFullWidth,
  isLoading,
  hasStartIcon,
  hasEndIcon,
  disabled,
  contentAlign = ButtonContentAlign.CENTER,
  className,
}: GetButtonClassesOptions): string => {
  const shouldBeDisabled = Boolean(disabled || isLoading);

  return clsx(
    styles.button,
    styles[variant],
    styles[size],
    {
      [styles.is_full_width]: isFullWidth,
      [styles.is_loading]: isLoading,
      [styles.is_with_icons]: hasStartIcon || hasEndIcon,
      [styles.is_disabled]: shouldBeDisabled,
      [styles.align_start]: contentAlign === ButtonContentAlign.START,
    },
    className,
  );
};

type ButtonContentProps = {
  children: ReactNode;
  size?: ButtonSize;
  isLoading?: boolean;
  StartIcon?: React.ComponentType;
  EndIcon?: React.ComponentType;
};

export const ButtonContent = ({
  children,
  size = ButtonSize.MEDIUM,
  isLoading,
  StartIcon,
  EndIcon,
}: ButtonContentProps): JSX.Element => {
  if (isLoading) {
    return <span className={styles.loader} />;
  }

  return (
    <span className={styles.content}>
      {StartIcon && <StartIcon />}
      <Typography
        variant={getButtonTypographyVariant(size)}
        tag="span"
        className={styles.content__text}
      >
        {children}
      </Typography>
      {EndIcon && <EndIcon />}
    </span>
  );
};
